import React, { useState, useEffect } from 'react';
import { campaignsAPI } from '../../../../services/api';
import CharacterEditorModal from '../../CharacterEditorModal';
import './NPCs.css';

function NPCs({ campaign }) {
  const [npcs, setNpcs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Character editor (reused for NPCs)
  const [showEditor, setShowEditor] = useState(false);
  const [editorCharacterId, setEditorCharacterId] = useState(null);

  // Form state for creating NPCs (now character-style)
  const [npcForm, setNpcForm] = useState({
    name: '',
    characterType: 'Tolerant',
    bonusXp: 0,
    role: '',
    description: '',
    location: '',
    faction: '',
    notes: ''
  });


  useEffect(() => {
    loadNpcs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id]);

  const loadNpcs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await campaignsAPI.getCampaignNpcs(campaign.id);
      setNpcs(res.data?.npcs || []);
    } catch (e) {
      console.error('Failed to load NPCs:', e);
      setError(e?.response?.data?.error || 'Failed to load NPCs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNpc = () => {
    setNpcForm({
      name: '',
      characterType: 'Tolerant',
      role: '',
      description: '',
      location: '',
      faction: '',
      notes: ''
    });
    setShowCreateModal(true);
  };

  const handleFormChange = (field, value) => {
    setNpcForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const openNpcEditor = (npcId) => {
    setEditorCharacterId(npcId);
    setShowEditor(true);
  };

  const closeCreateModal = () => {
    if (isCreating) return;
    setShowCreateModal(false);
  };

  const handleSubmitNpc = async () => {
    if (!npcForm.name.trim()) {
      alert('Please enter an NPC name');
      return;
    }

    try {
      setIsCreating(true);

      const res = await campaignsAPI.createNpc(campaign.id, {
        name: npcForm.name,
        characterType: npcForm.characterType,
        bonusXp: npcForm.bonusXp,
        role: npcForm.role,
        description: npcForm.description,
        location: npcForm.location,
        faction: npcForm.faction,
        notes: npcForm.notes,
      });

      // Close create modal & refresh list
      setShowCreateModal(false);
      await loadNpcs();

      // If backend returns npcId, open the full character editor for same-builder parity
      const newNpcId = res?.data?.npcId;
      if (newNpcId) {
        openNpcEditor(newNpcId);
      }
    } catch (e) {
      console.error('Failed to create NPC:', e);
      alert(e?.response?.data?.error || 'Failed to create NPC');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteNpc = async (npcId) => {
    if (!window.confirm('Are you sure you want to delete this NPC? This cannot be undone.')) {
      return;
    }

    try {
      setIsDeleting(true);
      await campaignsAPI.deleteNpc(campaign.id, npcId);
      await loadNpcs();

      // If we were editing this NPC, close the editor
      if (editorCharacterId === npcId) {
        setShowEditor(false);
        setEditorCharacterId(null);
      }
    } catch (e) {
      console.error('Failed to delete NPC:', e);
      alert(e?.response?.data?.error || 'Failed to delete NPC');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="npcs-section loading">
        <div className="loading-spinner"></div>
        <p>Loading NPCs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="npcs-section error">
        <p className="error-message">{error}</p>
        <button className="btn-secondary" onClick={loadNpcs}>Retry</button>
      </div>
    );
  }

  return (
    <div className="npcs-section">
      <div className="npcs-header">
        <div className="header-info">
          <h2>Non-Player Characters</h2>
          <p className="npc-count">{npcs.length} NPC{npcs.length !== 1 ? 's' : ''} in campaign</p>
        </div>
        <button className="btn-primary" onClick={handleCreateNpc}>
          + Create NPC
        </button>
      </div>

      {npcs.length === 0 ? (
        <div className="empty-state">
          <h3>No NPCs Yet</h3>
          <p>Create NPCs to populate your campaign world with memorable characters, allies, and adversaries.</p>
          <button className="btn-primary" onClick={handleCreateNpc}>
            Create Your First NPC
          </button>
        </div>
      ) : (
        <div className="npcs-grid">
          {npcs.map(npc => (
            <div key={npc.id} className="npc-card">
              <div className="npc-header">
                <h3 className="npc-name">{npc.name}</h3>
                {npc.role && <span className="npc-role">{npc.role}</span>}
              </div>

              {npc.description && (
                <p className="npc-description">{npc.description}</p>
              )}

              <div className="npc-details">
                {npc.location && (
                  <div className="npc-detail">
                    <span className="detail-label">Location:</span>
                    <span className="detail-value">{npc.location}</span>
                  </div>
                )}
                {npc.faction && (
                  <div className="npc-detail">
                    <span className="detail-label">Faction:</span>
                    <span className="detail-value">{npc.faction}</span>
                  </div>
                )}
              </div>

              <div className="npc-quick-stats">
                <div className="quick-stat">
                  <span className="quick-stat-label">HP:</span>
                  <span className="quick-stat-value">{npc.maxHp}</span>
                </div>
                <div className="quick-stat">
                  <span className="quick-stat-label">Movement:</span>
                  <span className="quick-stat-value">{npc.movement} ft</span>
                </div>
              </div>

              <div className="npc-actions">        
                <button
                  className="btn-secondary"
                  onClick={() => openNpcEditor(npc.id)}
                >
                  Edit Sheet
                </button>
                <button
                  className="btn-secondary"
                  onClick={async () => {
                    const raw = window.prompt('Grant how much XP to this NPC?', '500');
                    const amount = Math.floor(Number(raw));
                    if (!Number.isFinite(amount) || amount <= 0) return;

                    try {
                      await campaignsAPI.grantNpcXp(campaign.id, npc.id, amount);
                      alert('XP granted.');
                      // optional refresh (useful if you show XP later)
                      // await loadNpcs();
                    } catch (e) {
                      console.error(e);
                      alert(e?.response?.data?.error || 'Failed to grant XP');
                    }
                  }}
                >
                  Grant XP
                </button>
                <button
                  className="btn-danger"
                  onClick={() => handleDeleteNpc(npc.id)}
                  disabled={isDeleting}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create NPC Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={closeCreateModal}>
          <div className="modal-content create-npc-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New NPC</h2>
              <button className="modal-close" onClick={closeCreateModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-section">
                <h3>Basic Information</h3>

                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={npcForm.name}
                    onChange={e => handleFormChange('name', e.target.value)}
                    placeholder="NPC name"
                    maxLength={100}
                    disabled={isCreating}
                  />
                </div>

                <div className="form-group">
                  <label>Character Type</label>
                  <select
                    value={npcForm.characterType}
                    onChange={e => handleFormChange('characterType', e.target.value)}
                    disabled={isCreating}
                  >
                    <option value="Intolerant">Intolerant</option>
                    <option value="Tolerant">Tolerant</option>
                    <option value="Ascended">Ascended</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Bonus XP</label>
                  <input
                    type="number"
                    min="0"
                    step="50"
                    value={npcForm.bonusXp}
                    onChange={(e) => handleFormChange('bonusXp', Number(e.target.value || 0))}
                    disabled={isCreating}
                  />
                  <small style={{ opacity: 0.85 }}>
                    Adds extra XP on top of campaign starting XP (after type cost).
                  </small>
                </div>

                <div className="form-group">
                  <label>Role</label>
                  <input
                    type="text"
                    value={npcForm.role}
                    onChange={e => handleFormChange('role', e.target.value)}
                    placeholder="e.g., Merchant, Guard Captain, Ascended Hunter"
                    maxLength={100}
                    disabled={isCreating}
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={npcForm.description}
                    onChange={e => handleFormChange('description', e.target.value)}
                    placeholder="Physical appearance, personality, mannerisms..."
                    rows={3}
                    maxLength={500}
                    disabled={isCreating}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      value={npcForm.location}
                      onChange={e => handleFormChange('location', e.target.value)}
                      placeholder="Where they're usually found"
                      maxLength={100}
                      disabled={isCreating}
                    />
                  </div>

                  <div className="form-group">
                    <label>Faction</label>
                    <input
                      type="text"
                      value={npcForm.faction}
                      onChange={e => handleFormChange('faction', e.target.value)}
                      placeholder="Church, Resistance, Independent, etc."
                      maxLength={100}
                      disabled={isCreating}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Notes</h3>
                <div className="form-group">
                  <textarea
                    value={npcForm.notes}
                    onChange={e => handleFormChange('notes', e.target.value)}
                    placeholder="GM notes, plot hooks, relationships, motivations..."
                    rows={4}
                    maxLength={1000}
                    disabled={isCreating}
                  />
                </div>
              </div>

              <div className="form-section">
                <p style={{ opacity: 0.85 }}>
                  After creating, you’ll be able to fully build this NPC using the same XP/skills/abilities editor as player characters.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-primary"
                onClick={handleSubmitNpc}
                disabled={isCreating || !npcForm.name.trim()}
              >
                {isCreating ? 'Creating...' : 'Create NPC'}
              </button>
              <button className="btn-secondary" onClick={closeCreateModal} disabled={isCreating}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Character Editor Modal (shared with PCs) */}
      <CharacterEditorModal
        isOpen={showEditor}
        characterId={editorCharacterId}
        onClose={() => {
          setShowEditor(false);
          setEditorCharacterId(null);
        }}
        onUpdated={() => {
          // after saving a sheet, refresh NPC cards (HP/movement/etc)
          loadNpcs();
        }}
      />
    </div>
  );
}

export default NPCs;